// SchemaFieldInput — renders a single CmForm schema field (`SchemaField`) as an
// editable input, driven by the field's `widget`. Shared by the form-builder
// record editor and (upcoming) the map EntityForm so schema-driven capture is
// rendered identically everywhere.
//
// Handles data widgets only (text/textarea/number/select/multiselect/
// classification/date/datetime/time/yesno/barcode/address/location). Media
// widgets (photo/video/audio) are handled by their host's media flow, not here.

import { Box, Checkbox, ListItemText, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ClassificationOption, SchemaField } from "../../../types/fieldForm";
import { slugifyValue } from "../../../services/classificationSets";

export interface SchemaFieldInputProps {
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
}

export const SchemaFieldInput: React.FC<SchemaFieldInputProps> = ({ field, value, onChange }) => {
  const label = (
    <Stack direction='row' spacing={4} sx={{ mb: "6px" }}>
      <Typography variant='body2' fontWeight={600}>
        {field.label || field.key}
      </Typography>
      {field.required && (
        <Typography variant='body2' color='error.main'>
          *
        </Typography>
      )}
    </Stack>
  );

  // Yes / No toggle
  if (field.widget === "yesno") {
    const v = value === true || value === "yes" ? "yes" : value === false || value === "no" ? "no" : "";
    return (
      <Box>
        {label}
        <ToggleButtonGroup exclusive size='small' value={v} onChange={(_e, next) => next != null && onChange(next === "yes")}>
          <ToggleButton value='yes'>Yes</ToggleButton>
          <ToggleButton value='no'>No</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  }

  // Classification — hierarchical drill-down (stores a value-slug path array).
  if (field.widget === "classification") {
    return <ClassificationInput field={field} value={value} onChange={onChange} labelNode={label} />;
  }

  // Single select
  if (field.widget === "select" && field.options?.length) {
    return (
      <Box>
        {label}
        <TextField select fullWidth size='small' value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
          <MenuItem value=''>—</MenuItem>
          {field.options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  }

  // Multi select (flat options)
  if (field.widget === "multiselect" && field.options?.length) {
    const arr = Array.isArray(value) ? (value as string[]) : value ? [String(value)] : [];
    return (
      <Box>
        {label}
        <TextField
          select
          fullWidth
          size='small'
          value={arr}
          onChange={(e) => onChange(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
          SelectProps={{ multiple: true }}
        >
          {field.options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  }

  // Date / datetime / time
  const nativeType =
    field.widget === "date" ? "date" : field.widget === "datetime" ? "datetime-local" : field.widget === "time" ? "time" : null;
  if (nativeType) {
    return (
      <Box>
        {label}
        <TextField
          fullWidth
          size='small'
          type={nativeType}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    );
  }

  // Default — text / textarea / number / barcode / address / location
  const multiline = field.widget === "textarea";
  const numeric = field.widget === "number";
  return (
    <Box>
      {label}
      <TextField
        fullWidth
        size='small'
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        type={numeric ? "number" : "text"}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        disabled={field.readonly}
      />
    </Box>
  );
};

// ─── Classification (single nested dropdown) ────────────────────────────────

interface ClassificationLeaf {
  /** Value-slug path from root → leaf — the stored value (`string[]`). */
  valuePath: string[];
  /** Stable `<Select>` key = the value-slug path joined. */
  key: string;
  /** Selectable leaf label (deepest node). */
  leafLabel: string;
  /** Ancestor label path shown as a breadcrumb under the label. */
  breadcrumb: string;
}

/**
 * Collect only the *leaf* nodes (a node with children is a category, not a
 * selectable value). Each leaf keeps its value-slug path (the stored value) and,
 * derived from the tree, a leaf label + ancestor breadcrumb for display — so the
 * hierarchy reads without indentation and captured data stays label-independent.
 */
export function classificationLeaves(
  tree: ClassificationOption[],
  parents: { label: string; value: string }[] = [],
  acc: ClassificationLeaf[] = []
): ClassificationLeaf[] {
  for (const node of tree) {
    const value = node.value || slugifyValue(node.label || "");
    const chain = [...parents, { label: node.label || node.value || "(unnamed)", value }];
    if (node.children?.length) {
      classificationLeaves(node.children, chain, acc);
    } else {
      const valuePath = chain.map((c) => c.value);
      acc.push({
        valuePath,
        key: valuePath.join("/"),
        leafLabel: node.label || node.value || "(unnamed)",
        breadcrumb: parents.map((p) => p.label).join(" › ")
      });
    }
  }
  return acc;
}

interface ClassificationInputProps {
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
  labelNode: React.ReactNode;
}

/**
 * Single dropdown over `options_tree`. Only leaf nodes are selectable (a node
 * with children is a category, not a value); each row shows a checkbox for the
 * current selection, the leaf label, and its ancestor breadcrumb (no
 * indentation). Stores the chosen leaf's **value-slug path** as a `string[]`
 * (e.g. `["laure","tilke","tilka","tillu"]`); the breadcrumb is derived from the
 * tree at render, so captured data never depends on labels (backend §7 Q1).
 */
const ClassificationInput: React.FC<ClassificationInputProps> = ({ field, value, onChange, labelNode }) => {
  const tree = field.options_tree ?? [];
  // The stored value is a value-slug path array; serialise to a key for <Select>.
  const selectedKey = Array.isArray(value) ? (value as string[]).join("/") : String(value ?? "");

  // Fallback: no materialised tree but flat options (value-slugs) → single select.
  if (tree.length === 0) {
    return (
      <Box>
        {labelNode}
        <TextField select fullWidth size='small' value={selectedKey} onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}>
          <MenuItem value=''>
            <em>Select…</em>
          </MenuItem>
          {(field.options ?? []).map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  }

  const leaves = classificationLeaves(tree);
  const byKey = new Map(leaves.map((l) => [l.key, l]));
  const known = !selectedKey || byKey.has(selectedKey);

  const emit = (key: string) => {
    if (!key) return onChange([]);
    const leaf = byKey.get(key);
    onChange(leaf ? leaf.valuePath : key.split("/"));
  };

  return (
    <Box>
      {labelNode}
      <TextField
        select
        fullWidth
        size='small'
        value={selectedKey}
        onChange={(e) => emit(e.target.value)}
        SelectProps={{
          displayEmpty: true,
          renderValue: (v: unknown) => {
            const s = String(v ?? "");
            if (!s) {
              return (
                <Box component='span' sx={{ color: "text.disabled" }}>
                  Select…
                </Box>
              );
            }
            const hit = byKey.get(s);
            return hit ? hit.leafLabel : s;
          },
          MenuProps: { PaperProps: { sx: { maxHeight: 360 } } }
        }}
      >
        <MenuItem value=''>
          <em>Select…</em>
        </MenuItem>
        {!known && <MenuItem value={selectedKey}>{selectedKey}</MenuItem>}
        {leaves.map((c) => (
          <MenuItem key={c.key} value={c.key} sx={{ alignItems: "flex-start", py: "6px" }}>
            <Checkbox checked={c.key === selectedKey} size='small' sx={{ p: 0, mr: "10px", mt: "2px" }} />
            <ListItemText
              primary={c.leafLabel}
              secondary={c.breadcrumb || undefined}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 12, color: "text.secondary" }}
            />
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

export default SchemaFieldInput;
